import {Component, Input, OnInit} from '@angular/core';
import {
  ClassificatorValuesQuery,
  FilterModel,
  GLOBAL, LanguagesQuery,
  LeModalService,
  PropertiesQuery
} from "common-shared";
import {
  ItemGroupListFilter,
  ItemGroupListQuery,
  ItemGroupQuery, SpecSearchMenuItemComponent
} from "web-shared";
import {TableComponent} from "common-shared";
import {map, takeUntil} from "rxjs/operators";
import {BehaviorSubject, debounceTime, distinctUntilChanged} from "rxjs";
import {ManufacturingItemGroupService} from "../manufacturing-item-group.service";
import {ItemGroupsQuery} from "web-shared";
import {
  ManufacturingGraphModalComponent
} from "../../graph/manufacturing-graph-modal/manufacturing-graph-modal.component";
import {
  ManufacturingItemGroupEditModalComponent
} from "../manufacturing-item-group-edit-modal/manufacturing-item-group-edit-modal.component";
import {FilterMetadata, LazyLoadEvent} from "primeng/api";
import {StateService} from "web-shared";
import {
  HierarchyNodeEditModalComponent
} from "../../../common/hierarchy-nodes/hierarchy-node-edit-modal/hierarchy-node-edit-modal.component";
import {HierarchyNodeService} from "../../../common/hierarchy-nodes/hierarchy-node.service";
import {HierarchyNodeModel} from "../../../common/hierarchy-nodes/models/hierarchy-node.model";

@Component({
  selector: 'app-item-group-table',
  templateUrl: './item-group-table.component.html',
  styles: [
  ]
})
export class ItemGroupTableComponent extends TableComponent implements OnInit {
  @Input() filter: FilterModel<ItemGroupListFilter>;
  @Input() stateKey: string;
  @Input() specSearchMenuItemComponent: SpecSearchMenuItemComponent;
  @Input() showOnlyRelevantSpecsChange: boolean;
  @Input() includeInheritedValuesChange: boolean;
  @Input() includeSpecConstraintsValuesChange: boolean;
  filteredData$: BehaviorSubject<ItemGroupListQuery[]> = new BehaviorSubject<ItemGroupListQuery[]>([]);
  itemGroupListPropertyId = GLOBAL.Properties.ItemGroupListFilterSettings;
  hierarchyNodes: HierarchyNodeModel[];
  constructor(public classificatorValuesQuery: ClassificatorValuesQuery, public manufacturingItemGroupService: ManufacturingItemGroupService, private hierarchyNodeService: HierarchyNodeService, private modalService: LeModalService, public itemGroupsQuery: ItemGroupsQuery, public propertiesQuery: PropertiesQuery, public languagesQuery: LanguagesQuery, public stateService: StateService) {
    super();
  }

  ngOnInit(): void {
    this.search$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      if (this.table) {
        this.table.filterGlobal(value, 'contains');
      }
    });

    this.lazyLoadStream.pipe(takeUntil(this.componentDestroyed), debounceTime(this.loadDebounceTime), distinctUntilChanged(), map((value) => {
      this.loadDebounceTime = 0;
      return value;
    })).subscribe(event => this.load(event));
    this.applyDefaultFilters();
  }

  load(event: LazyLoadEvent = null) {
    this.prepareLoad(this.filter, event);
    this.manufacturingItemGroupService.getListByFilter(this.filter).pipe(takeUntil(this.componentDestroyed)).subscribe(pagedData => {
      this.filteredData$.next(pagedData.Data);
      this.updateAfterLoad(this.filter, pagedData);
      this.ensureHierarchyNodes();
    });
  }

  showHierarchy(): void {
    const initialSelection = this.filter.Data.HierarchyNode?.length ? [...this.filter.Data.HierarchyNode?.map(comparator => comparator.value)] : [];
    const modalRef = this.modalService.show(HierarchyNodeEditModalComponent, {initialState: {selectedIds: initialSelection, readonly: true}, ignoreBackdropClick: true});
    modalRef.content.onHide$.subscribe(finalSelection => {
      const isChanged = initialSelection.length !== finalSelection.data.length || initialSelection.some(node => finalSelection.data.indexOf(node) === -1);
      if (isChanged) {
        this.updateFiltersByHierarchyNodes(finalSelection.data);
      }
    });
  }

  updateFiltersByHierarchyNodes(selectedNodeIds: string[]): void {
    this.tableFilters['HierarchyNode'] = selectedNodeIds.map(selectedNodeId => {
      return {
        value: selectedNodeId,
        matchMode: 'or',
        operator: 'contains'
      }
    });
    this.table.filters['HierarchyNode'] = [...this.tableFilters['HierarchyNode']];
    this.filter.Data.HierarchyNode = [...this.tableFilters['HierarchyNode']];

    this.tryLazyLoad();
    this.ensureHierarchyNodes();
  }

  removeNode(nodeId: string, hierarchyNodeFilter: FilterMetadata[]): void {
    const selectedNodeIds = hierarchyNodeFilter.map(comparator => comparator.value);
    this.updateFiltersByHierarchyNodes(selectedNodeIds.filter(id => id !== nodeId));
  }

  ensureHierarchyNodes(): void {
    if (!this.hierarchyNodes && this.filter.Data.HierarchyNode?.length) {
      this.hierarchyNodeService.getRootWithHierarchy().pipe(takeUntil(this.componentDestroyed)).subscribe(root => {
        this.hierarchyNodes = this.hierarchyNodeService.flatten(root.Children);
      });
    }
  }

  showGraph(row: ItemGroupQuery) {
    this.modalService.show(ManufacturingGraphModalComponent, {initialState: {id: row.Id, graphSource: 'ItemGroup', title: row.DisplayName} as ManufacturingGraphModalComponent, class: 'modal-full'});
  }

  open(row: ItemGroupQuery) {
    const modalRef = this.modalService.show(ManufacturingItemGroupEditModalComponent, {initialState: {entityId: row.Id} as ManufacturingItemGroupEditModalComponent, class: 'modal-xl'});
    modalRef.onHide$.subscribe(value => {
      if(value.result){
        this.tryLazyLoad();
      }
    })
  }

  clearState() {
    this.table.clearState();
    this.filter.Data = new ItemGroupListFilter();
    this.filter.Global = null;
    this.applyDefaultFilters();
    this.table.filters["ShowOnlyRelevantSpecs"] = {value: false};
    this.table.filters["IncludeInheritedSpecs"] = {value: false};
    this.table.filters["IncludeSpecConstraints"] = {value: false};
    setTimeout(() => {
      this.tryLazyLoad();
    });
  }

  loadSavedState(state: LazyLoadEvent) {
    this.filter.Data = new ItemGroupListFilter();
    this.table.clearState();
    this.table.filters = state.filters ?? {};
    this.onStateRestore(state);
    const doesNotTriggerLazyLoad = this.tableSortOrder === state.sortOrder && this.tableSortField === state.sortField;
    if (doesNotTriggerLazyLoad) {
      this.load(state);
    }
    this.table.saveState();
  }

  onStateRestore(state) {
    state.selection = null;

    setTimeout(() => {
      this.tableSortField = state.sortField ?? this.tableSortField ?? this.sortField;
      this.tableSortOrder = state.sortOrder ?? this.tableSortOrder ?? this.sortOrder;
      this.hasFilters = FilterModel.hasAny(state.filters);
      if (this.hasFilters) {
        this.filter.AdvancedFilter = true;
      }
    });
  }

  onTableDropdownOpen(isOpenEvent: boolean) {
    if (isOpenEvent) {
      this.reloadOnClose = false;
    } else {
      this.tableDropdownClosed.emit();
      if (this.reloadOnClose) {
        this.tryLazyLoad();
      }
    }
  }

    tryLazyLoad() {
      if (typeof this.table.filter === 'function') {
        this.table._filter();
      } else {
        this.load();
      }
    }
  }
